import { memo, useState } from "react";
import { useStore } from "../../store/globalState";
import { useVirtualKeyboardOpen } from "../../hooks/useVirtualKeyboardOpen";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuraLayout, Button, Layout } from "lola-framework-ui-test";
import { FloatingField } from "../../components/FloatingField";
import { M2M_ROUTES, TITLE_COLOR } from "../../constants";
import {
  CARD_BRANDS,
  DEFAULT_ALLOWED_BRANDS,
  type CardBrandKey,
} from "../../constants/card-brands";
import { paymentType } from "../../interfaces/payment";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";
import { Navbar } from "../../components/Navbar";


/**
 * Gallery-safe m2m "Collect card" screen (ported from AddCardCollectPage).
 *
 * The production widget collects card data through a VGS iframe (which needs a
 * real vault) and syncs it with the backend. For the autonomous gallery we
 * replace VGS with plain, mocked inputs that keep the same look & flow: on
 * submit we create a fake card in the store and navigate to the transfer
 * screen. No VGS, no APIs, no claim-link side effects.
 */
const BRAND_DISPLAY_ORDER: CardBrandKey[] = ["mastercard", "visa"];

const detectBrand = (cardNumber: string): string => {
  const digits = cardNumber.replace(/\D/g, "");
  if (digits.startsWith("4")) return "VISA";
  if (/^5[1-5]/.test(digits) || /^2[2-7]/.test(digits)) return "MASTERCARD";
  return "CARD";
};

const formatCardNumber = (value: string): string =>
  value
    .replace(/\D/g, "")
    .slice(0, 16)
    .replace(/(.{4})/g, "$1 ")
    .trim();

export const AddCardCollectPage = memo(() => {
  const { cardState, setCardState, theme, setPaymentState, cardList, setCardList } =
    useStore();
  const navigate = useNavigate();
  const { t } = useTranslation("m2m");
  const keyboardOpen = useVirtualKeyboardOpen();

  const [holder, setHolder] = useState("Jane Doe");
  const [number, setNumber] = useState("4242 4242 4242 4242");
  const [expiry, setExpiry] = useState("12 / 27");
  const [cvv, setCvv] = useState("123");

  const colorsConfig = theme?.whiteView;
  const { backgroundBtn, textColorBtn, bodyCopy } = colorsConfig;
  const fieldColors = {
    background: colorsConfig.background,
    title: colorsConfig.title,
    bodyCopy,
    inactiveColor: colorsConfig.subtitile,
    errorColor: colorsConfig.errorColor,
  };

  const displayedBrands = BRAND_DISPLAY_ORDER.filter((key) =>
    DEFAULT_ALLOWED_BRANDS.includes(key)
  );

  const handleSubmit = (event?: React.FormEvent) => {
    event?.preventDefault();

    const digits = number.replace(/\D/g, "");
    const last4 = digits.slice(-4) || "0000";
    const brand = detectBrand(number);
    const name = `${brand}-${last4}`;
    const [expiryMonth = "12", expiryYear = "27"] = expiry
      .split("/")
      .map((part) => part.trim());

    const cardId = `card_${brand.toLowerCase()}_${last4}`;
    const cardData = {
      cardId,
      name,
      brand,
      last4,
      accountNumber: digits,
      expirationDate: `${expiryMonth}/${expiryYear}`,
      fullName: holder,
      cvv,
    };

    setCardState({ ...cardState, ...cardData });
    setPaymentState({ payment: { cardId, type: paymentType.CARD, brand: name } });

    const exists = cardList.find((card) => card.cardId === cardId);
    if (!exists) setCardList([...cardList, cardData]);

    navigate(M2M_ROUTES.TRANSFER_A);
  };

  return (
    <AuraLayout
      colorConfig={colorsConfig}
      className="white-view-background flavor-a"
    >
      <Layout.Header>
        <Navbar
          align="center"
          color={TITLE_COLOR}
          title={t("card.add")}
          onBackClick={() => navigate(-1)}
        />
      </Layout.Header>

      <Layout.Content className="collect">
        <ScrollFadeIndicator color={bodyCopy} />
        <form id="add-card-form" className="mock-card-form" onSubmit={handleSubmit}>
          <section className="mock-card-fields">
            <div className="mock-card-field">
                            <FloatingField
                label={t("card.owner")}
                value={holder}
                required
                colors={fieldColors}
              >
<input
                id="collect-card-holder-name"
                style={{ color: bodyCopy }}
                value={holder}
                onChange={(e) => setHolder(e.target.value)}
                autoComplete="cc-name"
                autoFocus
              />
              </FloatingField>
            </div>

            <div className="mock-card-field">
                            <FloatingField
                label={t("card.number")}
                placeholder={t("card.numberPlaceholder")}
                value={number}
                required
                colors={fieldColors}
              >
<input
                id="collect-card-number"
                style={{ color: bodyCopy, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", letterSpacing: "0.04em" }}
                value={number}
                onChange={(e) => setNumber(formatCardNumber(e.target.value))}
                inputMode="numeric"
              />
              </FloatingField>
            </div>

            <section className="vgs-inline">
              <div className="mock-card-field" style={{ flex: 1 }}>
                                <FloatingField
                  label={t("card.expiryLabel")}
                  placeholder={t("card.expiryPlaceholder")}
                  value={expiry}
                required
                  colors={fieldColors}
                >
<input
                  id="collect-card-expiry"
                  style={{ color: bodyCopy, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", letterSpacing: "0.04em" }}
                  value={expiry}
                  onChange={(e) => setExpiry(e.target.value)}
                />
                </FloatingField>
              </div>
              <div className="mock-card-field" style={{ flex: 1 }}>
                                <FloatingField
                  label={t("card.cvvLabel")}
                  placeholder={t("card.cvvFormatPlaceholder")}
                  value={cvv}
                required
                  colors={fieldColors}
                >
<input
                  id="collect-card-cvv"
                  style={{ color: bodyCopy, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", letterSpacing: "0.04em" }}
                  type="password"
                  value={cvv}
                  onChange={(e) =>
                    setCvv(e.target.value.replace(/\D/g, "").slice(0, 4))
                  }
                  inputMode="numeric"
                  autoComplete="cc-csc"
                  maxLength={4}
                />
                </FloatingField>
              </div>
            </section>
          </section>
        </form>
      </Layout.Content>

      <Layout.Footer>
        <section className="valid-cards">
          {!keyboardOpen && (
          <section className="valid-cards-container">
            <section className="valid-cards-logos">
              {displayedBrands.map((key) => {
                const brand = CARD_BRANDS[key];
                if (!brand) return null;
                return <img key={key} src={brand.logo} alt={t(brand.altKey)} />;
              })}
            </section>
            <p className="footer-text foreground" style={{ color: bodyCopy }}>
              {t("card.disclaimer")}
            </p>
          </section>
          )}
          <section className="footer-section">
            <Button
              type="submit"
              form="add-card-form"
              size="large"
              color={textColorBtn}
              background={backgroundBtn}
            >
              {t("card.continue")}
            </Button>
          </section>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
